import React, { useState } from 'react';
import { getUserColor, getDarkerTextColor } from '../utils/helpers';

// Function to get proxied image URL for expired S3 URLs
const getProxiedImageUrl = (s3Url) => {
  if (!s3Url) return null;
  
  // If it's already not an S3 URL, just return it as is
  if (!s3Url.includes('amazonaws.com')) {
    return s3Url;
  }
  
  // Extract file key from the S3 URL (everything after the bucket name)
  const urlParts = s3Url.split('/');
  const fileKey = urlParts.slice(3).join('/');
  
  // Return URL that will be handled by our backend proxy endpoint
  return `${process.env.REACT_APP_API_URL}/brand/image-proxy?key=${encodeURIComponent(fileKey)}`;
};

// Create a component for rendering active user avatars with typing indicators
const ActiveUserAvatars = ({ activeUsers, chatId, typingUsers, maxVisible = 6 }) => {
  // State to track image errors for each user
  const [imageErrors, setImageErrors] = useState({});
  
  if (!activeUsers || activeUsers.length === 0) return null;

  // Get typing users for this chat
  const chatTypingUsers = typingUsers[chatId] || {};
  const typingUserIds = Object.keys(chatTypingUsers);
  
  // Separate typing and non-typing users
  const typingActiveUsers = activeUsers.filter(user => typingUserIds.includes(user.userId));
  const nonTypingActiveUsers = activeUsers.filter(user => !typingUserIds.includes(user.userId));
  
  // Calculate how many users to show from each group
  const maxTypingVisible = Math.min(typingActiveUsers.length, maxVisible);
  const remainingSlots = maxVisible - maxTypingVisible;
  const maxNonTypingVisible = Math.min(nonTypingActiveUsers.length, remainingSlots);
  
  const visibleTypingUsers = typingActiveUsers.slice(0, maxTypingVisible);
  const visibleNonTypingUsers = nonTypingActiveUsers.slice(0, maxNonTypingVisible);
  const totalVisible = visibleTypingUsers.length + visibleNonTypingUsers.length;
  const remainingCount = Math.max(0, activeUsers.length - totalVisible);
  
  const hasTypingUsers = visibleTypingUsers.length > 0;

  const renderUserAvatar = (activeUser, index, isTyping = false) => {
    const initials = activeUser.userName
      .split(' ')
      .slice(0, 2)
      .map(name => name.charAt(0))
      .join('')
      .toUpperCase();

    // Get user color - use the color from the activeUser data or generate one
    const userColor = { hex: activeUser.userColor || getUserColor(activeUser.userId) };
    const hasProfileImage = activeUser.profileImage?.url;

    return (
      <div
        key={activeUser.userId}
        className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-medium overflow-hidden relative border-2 border-white dark:border-gray-800"
        style={{ zIndex: maxVisible - index }}
        title={`${activeUser.userName}${isTyping ? ' (typing...)' : ''}`}
      >
        {hasProfileImage && imageErrors[activeUser.userId] !== 'failed' ? (
          // Profile picture with colored outline
          <div className="w-full h-full rounded-full overflow-hidden relative">
            {/* Colored border background */}
            <div 
              className="absolute inset-0 rounded-full"
              style={{ backgroundColor: userColor.hex }}
            />
            {/* Profile image with slight inset to show the colored border */}
            <div className="absolute inset-[2px] rounded-full overflow-hidden bg-white dark:bg-[#161616]">
              <img 
                src={imageErrors[activeUser.userId] ? getProxiedImageUrl(activeUser.profileImage.url) : activeUser.profileImage.url} 
                alt={activeUser.userName}
                className="w-full h-full object-cover"
                onError={(e) => {
                  if (!imageErrors[activeUser.userId]) {
                    // First error - try the proxy URL
                    console.log('S3 URL failed, trying proxy for active user:', activeUser.userName);
                    setImageErrors(prev => ({ ...prev, [activeUser.userId]: true }));
                  } else {
                    // Proxy also failed - mark as failed to show monogram fallback
                    console.error('Both S3 URL and proxy failed for active user:', activeUser.userName);
                    setImageErrors(prev => ({ ...prev, [activeUser.userId]: 'failed' }));
                  }
                }}
                onLoad={() => {
                  if (imageErrors[activeUser.userId]) {
                    console.log('Successfully loaded active user image via proxy:', activeUser.userName);
                  }
                }}
              />
            </div>
          </div>
        ) : (
          // Monogram with colored background
          <div 
            className="w-full h-full flex items-center justify-center rounded-full"
            style={{ 
              backgroundColor: userColor.hex,
              color: getDarkerTextColor(userColor.hex)
            }}
          >
            {initials}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex items-center">
      {/* Typing users section */}
      {hasTypingUsers && (
        <div className="flex items-center">
          {/* Typing user avatars */}
          <div className="flex items-center -space-x-2">
            {visibleTypingUsers.map((user, index) => renderUserAvatar(user, index, true))}
          </div>
          
          {/* Typing indicator dots */}
          <div className="flex items-center ml-1">
            <div className="flex space-x-0.5">
              {[0, 1, 2].map((dotIndex) => {
                // Use the first typing user's color for the dots
                const firstTypingUser = visibleTypingUsers[0];
                const userColor = { hex: firstTypingUser.userColor || getUserColor(firstTypingUser.userId) };
                return (
                  <div
                    key={dotIndex}
                    className="w-1.5 h-1.5 rounded-full animate-bounce"
                    style={{ 
                      backgroundColor: userColor.hex,
                      animationDelay: `${dotIndex * 0.15}s`,
                      animationDuration: '1s'
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
      
      {/* Non-typing users section */}
      {visibleNonTypingUsers.length > 0 && (
        <div className={`flex items-center ${hasTypingUsers ? '-space-x-3 ml-2' : '-space-x-2'}`}>
          {visibleNonTypingUsers.map((user, index) => 
            renderUserAvatar(user, visibleTypingUsers.length + index, false)
          )}
        </div>
      )}
      
      {/* Remaining count indicator */}
      {remainingCount > 0 && (
        <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[10px] font-medium text-gray-600 dark:text-gray-300 border border-white dark:border-gray-800 ml-2">
          +{remainingCount}
        </div>
      )}
    </div>
  );
};

export default ActiveUserAvatars; 